import { useState, useEffect, useRef } from 'react';
import { txApi } from '../txApi.js';
import { C, inp, btn, Err, Spinner, SectionLabel, useConfirm } from './_shared.jsx';
import { triggerDownload } from '../../../shared/http/download.js';
import { useWorkspace } from '../../../shared/workspace/WorkspaceContext.jsx';

// ── Switching a list to Value + Code ─────────────────────────────────────────
//
// The switch is the moment of intent, so the codes are filled in here rather than left blank
// for something downstream to discover. A blank code does not fail loudly: the generator falls
// back to the display value, so "Saudi Arabia" lands in the taxonomy string where "SA" was
// meant. The suggestion below is offered, never applied on its own — a code is a business
// identifier, and one this screen invented would carry the same authority as one someone chose.

const suggestCode = (value) => (value || '')
  .toUpperCase()
  .replace(/[^A-Z0-9]+/g, '')
  .slice(0, 6);

function AddCodesDialog({ list, values, onCancel, onDone }) {
  const [codes,  setCodes]  = useState(() =>
    Object.fromEntries(values.map(v => [v.id, v.code || ''])));
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const filled = values.every(v => (codes[v.id] || '').trim());

  const applySuggestions = () => setCodes(prev => Object.fromEntries(
    values.map(v => [v.id, (prev[v.id] || '').trim() || suggestCode(v.value)])));

  const confirm = async () => {
    if (!filled) return;
    setSaving(true); setErr('');
    try {
      // Codes first, then the mode: the server refuses the switch while any value is still
      // blank, so doing it the other way round would fail on its own rule.
      for (const v of values) {
        await txApi.updateDictValue(v.id, { value: v.value, code: codes[v.id].trim() });
      }
      await txApi.updateDictList(list.id, { entry_mode: 'code_value' });
      onDone();
    } catch (e) {
      setErr(e.message || 'Could not switch this list');
      setSaving(false);
    }
  };

  return (
    <div className="o-scrim" style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: '24px 24px 20px', width: 460, maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>
          Give every value a code
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
          <strong>{list.name}</strong> has {values.length} value{values.length === 1 ? '' : 's'} with
          no code. The code is what goes into the taxonomy string — without one, the value itself
          is used instead.
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
          {values.map(v => (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.value}</span>
              <input
                style={inp({ width: 130, fontFamily: C.mono, fontSize: 12 })}
                placeholder={suggestCode(v.value) || 'code'}
                value={codes[v.id] || ''}
                onChange={e => setCodes(prev => ({ ...prev, [v.id]: e.target.value }))}
              />
            </div>
          ))}
        </div>

        <button style={btn('ghost', { padding: '4px 12px', fontSize: 12, marginBottom: 14 })}
          onClick={applySuggestions}>Fill the blanks with suggestions</button>

        {err && <div style={{ fontSize: 12, color: C.danger, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button style={btn('ghost', { padding: '8px 16px' })} onClick={onCancel}>Cancel</button>
          <button style={{ ...btn('primary', { padding: '8px 18px' }), opacity: filled && !saving ? 1 : 0.5 }}
            onClick={confirm} disabled={!filled || saving}>
            {saving ? 'Switching…' : 'Switch to Value + Code'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Value row in the right panel ──────────────────────────────────────────────
function ValueRow({ val, listId, entryMode, isClientView, onRefresh }) {
  const [editing,   setEditing]   = useState(false);
  const [draftVal,  setDraftVal]  = useState(val.value);
  const [draftCode, setDraftCode] = useState(val.code || '');
  const [saving,    setSaving]    = useState(false);
  const [rowErr,    setRowErr]    = useState('');

  const save = async () => {
    if (!draftVal.trim()) { setRowErr('Value required'); return; }
    if (entryMode === 'code_value' && !draftCode.trim()) { setRowErr('Code required'); return; }
    if (draftVal === val.value && draftCode === (val.code || '')) { setEditing(false); return; }
    setSaving(true); setRowErr('');
    const body = { value: draftVal.trim() };
    if (entryMode === 'code_value') body.code = draftCode.trim();
    try { await txApi.updateDictValue(val.id, body); onRefresh(); setEditing(false); }
    catch (e) { setRowErr(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const cancel = () => { setDraftVal(val.value); setDraftCode(val.code || ''); setEditing(false); setRowErr(''); };

  const toggleExclude = async () => {
    setSaving(true);
    try {
      if (val.is_excluded) await txApi.unexcludeDictValue(listId, val.id);
      else                  await txApi.excludeDictValue(listId, val.id);
      onRefresh();
    } catch { }
    finally { setSaving(false); }
  };

  const isGlobal = val.is_global;
  const excluded = val.is_excluded;

  return (
    <div style={{ padding: '9px 0', borderBottom: `1px solid ${C.border}`, opacity: excluded ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {/* Index */}
        <span style={{ width: 32, textAlign: 'right', fontFamily: C.mono, fontSize: 11,
          color: C.muted, paddingRight: 12, flexShrink: 0 }}>
          {val._idx}
        </span>

        {/* Global badge */}
        {isGlobal && (
          <span className="o-badge o-grey" style={{ marginRight: 8, flexShrink: 0 }}>Global</span>
        )}

        {/* Content */}
        {editing ? (
          <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={draftVal} onChange={e => setDraftVal(e.target.value)}
              autoFocus
              placeholder="Value"
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
              style={{ ...inp({ flex: 1 }), fontSize: 13 }} />
            {entryMode === 'code_value' && (
              <input value={draftCode} onChange={e => setDraftCode(e.target.value)}
                placeholder="Code"
                onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
                style={{ ...inp({ width: 110 }), fontSize: 12, fontFamily: C.mono }} />
            )}
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 13, color: excluded ? C.muted : C.text,
              textDecoration: excluded ? 'line-through' : 'none',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{val.value}</span>
            {/* Only in the mode where a code means something. A Value Only list generates from
                the value, so showing a code left over from a previous mode implies it is in
                play — and only some rows have one, which reads as data half-deleted. The codes
                are kept in the row (switching modes must not destroy what the other mode
                recorded); this just stops claiming they are in use. */}
            {entryMode === 'code_value' && val.code && (
              <span className="o-code o-teal" style={{ flexShrink: 0 }}>{val.code}</span>
            )}
          </div>
        )}

        {/* Actions */}
        {editing ? (
          <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
            <button style={btn('primary', { padding: '3px 12px', fontSize: 12 })} onClick={save} disabled={saving}>Save</button>
            <button style={btn('ghost',   { padding: '3px 10px', fontSize: 12 })} onClick={cancel}>Cancel</button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6, marginLeft: 8, alignItems: 'center' }}>
            {isGlobal && isClientView && (
              <button onClick={toggleExclude} disabled={saving} style={{
                padding: '2px 10px', borderRadius: 8, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                border: `1px solid ${excluded ? C.danger + '88' : C.border}`,
                background: excluded ? C.danger + '18' : 'transparent',
                color: excluded ? C.danger : C.muted,
              }}>{excluded ? 'Excluded' : 'Exclude'}</button>
            )}
            {(!isGlobal || !isClientView) && (
              <>
                <button onClick={() => setEditing(true)}
                  style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}>Edit</button>
                <button onClick={async () => { await txApi.deleteDictValue(val.id); onRefresh(); }}
                  style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
              </>
            )}
          </div>
        )}
      </div>
      {rowErr && <div style={{ fontSize: 11, color: C.danger, marginLeft: 44, marginTop: 4 }}>{rowErr}</div>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DataDictionaryPage() {
  // Two scopes, never more: the shared Global dictionary, and the workspace the top bar has
  // active. A dictionary is edited for the client you are working in, so listing every client
  // here only offered a way to edit the wrong one.
  const { client: wsClient } = useWorkspace();
  const clients = wsClient ? [wsClient] : [];

  const [selClientId,   setSelClientId]   = useState('global');

  const [lists,         setLists]         = useState([]);
  const [totalVals,     setTotalVals]     = useState(0);
  const [selList,       setSelList]       = useState(null);
  const [values,        setValues]        = useState([]);
  const [isInherited,   setIsInherited]   = useState(false);

  const [globalLists,   setGlobalLists]   = useState([]);

  const [search,        setSearch]        = useState('');
  const [loading,       setLoading]       = useState(true);
  const [valLoading,    setValLoading]    = useState(false);
  const [err,           setErr]           = useState(null);
  const [addInput,      setAddInput]      = useState('');
  const [addCode,       setAddCode]       = useState('');
  const [addErr,        setAddErr]        = useState('');
  const [adding,        setAdding]        = useState(false);
  const [newListName,   setNewListName]   = useState('');
  const [creatingList,  setCreatingList]  = useState(false);
  const [editingListId, setEditingListId] = useState(null);
  const [editListName,  setEditListName]  = useState('');
  const [linkingList,   setLinkingList]   = useState(false);
  const [pendingCodes,  setPendingCodes]  = useState(null); // values awaiting codes before the switch
  const [importMsg,     setImportMsg]     = useState(null); // { ok, text }
  const [importKey,     setImportKey]     = useState(0);   // reset file input
  const [allMsg,        setAllMsg]        = useState(null); // { errors } | { summary, total_imported }
  const [allKey,        setAllKey]        = useState(0);
  const [allLoading,    setAllLoading]    = useState(false);
  const addRef = useRef(null);
  const allFileRef = useRef(null);
  const [confirmUI, ask] = useConfirm();

  const isClientView = selClientId !== 'global';
  const entryMode    = selList?.entry_mode || 'value_only';

  // The client dictionary is the one people come here to edit, so it is the landing scope —
  // Global is a deliberate step sideways. This also follows a workspace switch, which must not
  // leave the page editing the client that is no longer active.
  useEffect(() => {
    setSelClientId(wsClient ? String(wsClient.id) : 'global');
  }, [wsClient?.id]);

  const loadGlobalLists = () =>
    txApi.listDictLists('global')
      .then(d => setGlobalLists(d.lists || []))
      .catch(() => {});

  useEffect(() => { loadGlobalLists(); }, []);

  const loadLists = () => {
    setLoading(true); setErr(null);
    return txApi.listDictLists(selClientId === 'global' ? 'global' : selClientId)
      .then(d => { setLists(d.lists || []); setTotalVals(d.total_values || 0); })
      .catch(e => setErr(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setSelList(null); setValues([]); setIsInherited(false);
    loadLists();
  }, [selClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadValues = (list) => {
    setSelList(list); setValLoading(true); setValues([]); setIsInherited(false);
    txApi.getDictValues(list.id)
      .then(d => { setValues(d.values || []); setIsInherited(!!d.is_inherited); })
      .catch(() => {})
      .finally(() => setValLoading(false));
  };

  const refresh = () => {
    loadLists();
    if (selList) {
      txApi.getDictValues(selList.id)
        .then(d => { setValues(d.values || []); setIsInherited(!!d.is_inherited); });
    }
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    setErr(null);
    try {
      const body = { name: newListName.trim() };
      if (isClientView) body.client_id = selClientId;
      const d = await txApi.createDictList(body);
      setNewListName(''); setCreatingList(false);
      await loadLists();
      loadValues(d.list);
    } catch (e) { setErr(e.message); }
  };

  const deleteList = async (id) => {
    const okay = await ask({
      title: 'Delete this list?',
      body: 'The list and every value in it are removed. Fields already pointing at it lose '
          + 'their options.',
      confirmLabel: 'Delete list',
      tone: 'danger',
    });
    if (!okay) return;
    try { await txApi.deleteDictList(id); if (selList?.id === id) { setSelList(null); setValues([]); } loadLists(); }
    catch (e) { setErr(e.message); }
  };

  const saveListName = async (id) => {
    if (!editListName.trim()) return;
    setErr(null);
    try {
      await txApi.updateDictList(id, { name: editListName.trim() });
      setEditingListId(null);
      if (selList?.id === id) setSelList(prev => ({ ...prev, name: editListName.trim() }));
      loadLists();
    } catch (e) { setErr(e.message); }
  };

  const updateEntryMode = async (newMode) => {
    if (!selList || newMode === entryMode) return;
    // Switching TO Value + Code with values that have no code is refused by the server, so ask
    // for the codes first. Switching back the other way keeps the codes on the rows — a mode
    // change should not destroy what the previous mode recorded, and switching twice by
    // mistake must not cost anything.
    if (newMode === 'code_value') {
      const needCodes = values.filter(v => !v.is_global && !(v.code || '').trim());
      if (needCodes.length) { setPendingCodes(needCodes); return; }
    }
    try {
      await txApi.updateDictList(selList.id, { entry_mode: newMode });
      setSelList(prev => ({ ...prev, entry_mode: newMode }));
      setLists(prev => prev.map(l => l.id === selList.id ? { ...l, entry_mode: newMode } : l));
      setAddInput(''); setAddCode(''); setAddErr('');
    } catch (e) { setErr(e.message); }
  };

  const linkToGlobal = async (globalListId) => {
    if (!selList) return;
    try {
      await txApi.updateDictList(selList.id, { global_list_id: globalListId || null });
      setLinkingList(false);
      const updated = { ...selList, global_list_id: globalListId || null,
        global_list_name: globalLists.find(l => l.id === globalListId)?.name || null };
      setSelList(updated);
      loadValues(updated);
      loadLists();
    } catch (e) { setErr(e.message); }
  };

  const addValues = async () => {
    if (!selList) return;
    setAddErr('');
    if (entryMode === 'code_value') {
      if (!addCode.trim()) { setAddErr('Code is required'); return; }
      if (!addInput.trim()) { setAddErr('Value is required'); return; }
      setAdding(true);
      try {
        await txApi.addDictValues(selList.id, { values: [{ value: addInput.trim(), code: addCode.trim() }] });
        setAddInput(''); setAddCode('');
        refresh();
      } catch (e) { setAddErr(e.message); }
      finally { setAdding(false); }
    } else {
      if (!addInput.trim()) return;
      setAdding(true);
      try {
        await txApi.addDictValues(selList.id, { values: addInput });
        setAddInput('');
        refresh();
        addRef.current?.focus();
      } catch (e) { setAddErr(e.message); }
      finally { setAdding(false); }
    }
  };

  const [exporting, setExporting] = useState(false);

  const importAllFields = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAllMsg(null);
    setAllLoading(true);
    const clientId = selClientId === 'global' ? '' : selClientId;
    txApi.importAllDictLists(file, clientId)
      .then(data => {
        setAllMsg(data);
        if (data.ok) refresh();
      })
      .catch(err => setAllMsg({ errors: [err.message] }))
      .finally(() => { setAllLoading(false); setAllKey(k => k + 1); });
  };

  const exportAllFields = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const clientName = selClientId === 'global'
        ? 'Global'
        : (clients.find(c => c.id === selClientId)?.name || selClientId);
      const blob = await txApi.exportAllDictLists(
        selClientId === 'global' ? '' : selClientId,
        clientName,
      );
      triggerDownload(blob, `DataDictionary_${clientName.replace(/\s+/g, '_')}.xlsx`);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  const filtered  = lists.filter(l => l.name.toLowerCase().includes(search.toLowerCase()));
  const globalVals = values.filter(v => v.is_global);
  const clientVals = values.filter(v => !v.is_global);
  const annotated  = [
    ...globalVals.map((v, i) => ({ ...v, _idx: i + 1 })),
    ...clientVals.map((v, i) => ({ ...v, _idx: globalVals.length + i + 1 })),
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 110px)' }}>

      {/* ── Client selector ── */}
      <div className="o-head-row" style={{ marginBottom: allMsg ? 10 : 20, alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          {/* Sits in the sidebar's column: 220px rail less its 16px padding, indented to match,
              so the control lines up with the stat cards and search box beneath it. */}
          <div style={{ width: 188, marginLeft: 16 }}>
            <div className="o-field-lbl" id="dd-for">Dictionary for</div>
            {/* Only ever two scopes here — the active workspace and Global — so a segmented
                control shows both at once instead of hiding one behind a dropdown. */}
            <div className="o-toggle" role="tablist" aria-labelledby="dd-for"
              style={{ display: 'flex', width: '100%', flexWrap: 'nowrap' }}>
              {wsClient && (
                // A long client name truncates rather than wrapping the two halves onto
                // separate lines.
                <button role="tab" aria-selected={isClientView} title={wsClient.name}
                  style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  onClick={() => setSelClientId(String(wsClient.id))}>{wsClient.name}</button>
              )}
              <button role="tab" aria-selected={!isClientView} style={{ flex: 1, whiteSpace: 'nowrap' }}
                onClick={() => setSelClientId('global')}>Global</button>
            </div>
          </div>
          {isClientView && (
            <span style={{ fontSize: 12, color: C.muted, paddingBottom: 9 }}>
              Client values layer on top of Global — edits here never modify Global lists.
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="o-btn" onClick={exportAllFields} disabled={exporting || lists.length === 0}>
            {exporting ? 'Exporting…' : 'Export fields'}
          </button>
          <button className="o-btn" disabled={allLoading}
            onClick={() => allFileRef.current?.click()}>
            {allLoading ? 'Importing…' : 'Import fields'}
          </button>
          <input key={allKey} ref={allFileRef} type="file" accept=".xlsx,.xls"
            style={{ display: 'none' }} tabIndex={-1} aria-hidden="true"
            disabled={allLoading} onChange={importAllFields} />
        </div>
      </div>

      {/* ── Import All Fields results ── */}
      {allMsg && (
        <div className="o-panel" style={{ borderColor: allMsg.errors ? C.danger : C.border,
          padding: '12px 16px', marginBottom: 16, fontSize: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, color: allMsg.errors ? C.danger : C.text }}>
              {allMsg.errors
                ? `Import blocked — ${allMsg.errors.length} error${allMsg.errors.length !== 1 ? 's' : ''} found`
                : `Import complete — ${allMsg.total_imported} new entr${allMsg.total_imported !== 1 ? 'ies' : 'y'} added`}
            </span>
            <div style={{ flex: 1 }} />
            <button onClick={() => setAllMsg(null)}
              style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 4px' }}>×</button>
          </div>
          {allMsg.errors ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {allMsg.errors.map((e, i) => (
                <div key={i} style={{ color: C.danger, display: 'flex', gap: 6 }}>
                  <span style={{ flexShrink: 0 }}>✗</span><span>{e}</span>
                </div>
              ))}
              <div style={{ marginTop: 6, color: C.muted, fontSize: 11 }}>Fix all errors above and re-upload.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {(allMsg.summary || []).map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600, color: C.text, minWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.field}</span>
                  <span style={{ color: C.success }}>{s.imported} imported</span>
                  {s.skipped > 0 && <span style={{ color: C.muted }}>{s.skipped} skipped (duplicate)</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Body ── */}
      <div style={{ display: 'flex', gap: 0, flex: 1, overflow: 'hidden' }}>

        {/* ── Left sidebar ── */}
        <div style={{ width: 220, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column' }}>

          <div className="o-stats" style={{ padding: '0 16px', marginBottom: 16 }}>
            {[['Lists', lists.length], ['Values', totalVals]].map(([lbl, val]) => (
              <div key={lbl} className="o-stat">
                <b>{val}</b>
                <span>{lbl}</span>
              </div>
            ))}
          </div>

          <div style={{ padding: '0 16px 10px' }}>
            <input className="o-control" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search lists" />
          </div>

          {/* List-level errors belong beside the list. The page's other error slot is inside
              the selected-list pane, which is not on screen while a list is being created or
              renamed — a refused name set the message and showed nothing. */}
          {err && (
            <div style={{ padding: '0 16px 8px', fontSize: 11, color: C.danger }}>{err}</div>
          )}

          {creatingList ? (
            <div style={{ padding: '0 16px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input className="o-control" autoFocus value={newListName} onChange={e => setNewListName(e.target.value)}
                placeholder="List name"
                onKeyDown={e => { if (e.key === 'Enter') createList(); if (e.key === 'Escape') { setCreatingList(false); setNewListName(''); } }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="o-btn o-btn-primary" style={{ flex: 1, justifyContent: 'center' }} onClick={createList}>Create</button>
                <button className="o-btn o-btn-ghost" onClick={() => { setCreatingList(false); setNewListName(''); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button className="o-btn o-btn-primary"
              style={{ margin: '0 16px 12px', width: 'calc(100% - 32px)', justifyContent: 'center' }}
              onClick={() => setCreatingList(true)}>New list</button>
          )}

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading && <Spinner />}
            {!loading && filtered.length === 0 && (
              <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', padding: '20px 16px' }}>No lists yet</div>
            )}
            {filtered.map(l => (
              <div key={l.id} onClick={() => { if (editingListId !== l.id) loadValues(l); }}
                aria-current={selList?.id === l.id ? 'true' : undefined}
                style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: `1px solid ${C.borderSoft}`,
                  background: selList?.id === l.id ? C.surface : 'transparent',
                  boxShadow: selList?.id === l.id ? `inset 2px 0 0 ${C.brass}` : 'none',
                }}>
                {editingListId === l.id ? (
                  <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                    <input className="o-control" autoFocus value={editListName} onChange={e => setEditListName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') saveListName(l.id); if (e.key === 'Escape') setEditingListId(null); }}
                      style={{ flex: 1, padding: '4px 7px', fontSize: 12 }} />
                    <button className="o-btn o-btn-sm o-btn-primary" onClick={() => saveListName(l.id)}>Save</button>
                    <button className="o-btn o-btn-sm o-btn-ghost" onClick={() => setEditingListId(null)}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: selList?.id === l.id ? C.text : C.dim,
                        fontWeight: selList?.id === l.id ? 600 : 400,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.name}</div>
                      {l.global_list_name && (
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>↳ {l.global_list_name}</div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 11, color: C.muted }}>{l.value_count} values</span>
                        {l.entry_mode === 'code_value' && (
                          <span className="o-code o-teal" style={{ fontSize: 9 }}>C+V</span>
                        )}
                      </div>
                    </div>
                    <button onClick={e => { e.stopPropagation(); setEditingListId(l.id); setEditListName(l.name); }}
                      style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 12, padding: '2px 3px' }}>✎</button>
                    <button onClick={e => { e.stopPropagation(); deleteList(l.id); }}
                      style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '0 2px' }}>×</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ flex: 1, padding: '0 0 0 28px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selList ? (
            <div className="o-state" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              Select a list from the sidebar, or create a new one
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="o-head-row" style={{ marginBottom: 16 }}>
                <div>
                  <h1 className="o-title" style={{ fontSize: '1.5rem' }}>{selList.name}</h1>
                  {/* What this list offers, which is what the sidebar and the field editor's
                      dropdown both count: inherited values included, excluded ones left out.
                      An excluded value is still listed below — with its Excluded toggle — but
                      it is not something this client can pick. */}
                  <p className="o-sub">{values.filter(v => !v.is_excluded).length} values</p>
                </div>

                {/* Entry mode toggle */}
                <div className="o-toggle">
                  {[
                    { mode: 'value_only', label: 'Value only' },
                    { mode: 'code_value', label: 'Value + code' },
                  ].map(({ mode, label }) => (
                    <button key={mode} aria-selected={entryMode === mode}
                      onClick={() => updateEntryMode(mode)}>{label}</button>
                  ))}
                </div>

              </div>

              {/* Inherit from Global — client lists only */}
              {isClientView && (
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
                  padding: '12px 16px', marginBottom: 14 }}>
                  {selList.global_list_id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: C.muted }}>Inheriting from Global:</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{selList.global_list_name}</span>
                      <div style={{ flex: 1 }} />
                      {linkingList ? (
                        <>
                          <select autoFocus defaultValue={selList.global_list_id}
                            onChange={e => linkToGlobal(e.target.value)}
                            style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                              color: C.text, fontSize: 12, padding: '5px 10px', fontFamily: 'inherit', outline: 'none' }}>
                            <option value="">— Remove link —</option>
                            {globalLists.map(gl => <option key={gl.id} value={gl.id}>{gl.name} ({gl.value_count} values)</option>)}
                          </select>
                          <button style={btn('ghost', { padding: '4px 10px', fontSize: 12 })} onClick={() => setLinkingList(false)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button style={btn('ghost', { padding: '4px 12px', fontSize: 12 })} onClick={() => setLinkingList(true)}>Change</button>
                          <button style={btn('ghost', { padding: '4px 12px', fontSize: 12, color: C.danger, borderColor: C.danger + '44' })}
                            onClick={() => linkToGlobal(null)}>Unlink</button>
                        </>
                      )}
                    </div>
                  ) : linkingList ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: C.muted, flexShrink: 0 }}>Inherit from Global list:</span>
                      <select autoFocus onChange={e => e.target.value && linkToGlobal(e.target.value)} defaultValue=""
                        style={{ flex: 1, background: C.surface, border: `1px solid ${C.brass}`, borderRadius: 8,
                          color: C.text, fontSize: 12, padding: '5px 10px', fontFamily: 'inherit', outline: 'none' }}>
                        <option value="">— select a Global list —</option>
                        {globalLists.map(gl => <option key={gl.id} value={gl.id}>{gl.name} ({gl.value_count} values)</option>)}
                      </select>
                      <button style={btn('ghost', { padding: '4px 10px', fontSize: 12 })} onClick={() => setLinkingList(false)}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 12, color: C.muted }}>Not linked to any Global list.</span>
                      <button style={btn('ghost', { padding: '4px 14px', fontSize: 12 })} onClick={() => { loadGlobalLists(); setLinkingList(true); }}>
                        🔗 Inherit from Global
                      </button>
                    </div>
                  )}
                </div>
              )}

              <Err msg={err} />

              {/* Add value input */}
              <div className="o-panel" style={{ padding: 16, marginBottom: 16 }}>
                {entryMode === 'code_value' ? (
                  <>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input ref={addRef} value={addInput} onChange={e => setAddInput(e.target.value)}
                        placeholder="Value (display name)"
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && addValues()}
                        style={{ ...inp({ flex: 1 }), fontSize: 13 }} />
                      <input value={addCode} onChange={e => setAddCode(e.target.value)}
                        placeholder="Code (e.g. ATR)"
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && addValues()}
                        style={{ ...inp({ width: 140 }), fontSize: 13, fontFamily: C.mono }} />
                      <button style={btn('primary', { padding: '6px 20px', fontSize: 13 })}
                        onClick={addValues} disabled={adding || !addInput.trim() || !addCode.trim()}>Add</button>
                    </div>
                    {addErr && <div style={{ fontSize: 11, color: C.danger, marginTop: 6 }}>{addErr}</div>}
                    {importMsg && <div style={{ fontSize: 11, color: importMsg.ok ? C.success : C.danger, marginTop: 6 }}>{importMsg.text}</div>}
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
                      💡 Value is what appears in dropdowns. Code is the shorthand used in the taxonomy string.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input ref={addRef} value={addInput} onChange={e => setAddInput(e.target.value)}
                        placeholder={isClientView && isInherited
                          ? 'Add a client-specific value (never written to Global)…'
                          : 'Type a value and press Enter… or paste a comma/newline-separated list'}
                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && addValues()}
                        style={{ ...inp({ flex: 1 }), fontSize: 13 }} />
                      <button style={btn('primary', { padding: '6px 20px', fontSize: 13 })}
                        onClick={addValues} disabled={adding || !addInput.trim()}>Add</button>
                    </div>
                    {addErr && <div style={{ fontSize: 11, color: C.danger, marginTop: 6 }}>{addErr}</div>}
                    {importMsg && <div style={{ fontSize: 11, color: importMsg.ok ? C.success : C.danger, marginTop: 6 }}>{importMsg.text}</div>}
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
                      💡 Paste a comma or newline-separated list to bulk-import values.
                      {isClientView && isInherited && (
                        <span style={{ color: C.dim, marginLeft: 6 }}>New values here are client-specific only.</span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Column headers for code_value mode */}
              {entryMode === 'code_value' && values.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px 6px 44px',
                  fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.08em', gap: 8 }}>
                  <span style={{ flex: 1 }}>Value</span>
                  <span style={{ width: 110 }}>Code</span>
                </div>
              )}

              {/* Values list */}
              <div className="o-panel" style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
                {valLoading && <div style={{ padding: 20 }}><Spinner /></div>}

                {!valLoading && isInherited && globalVals.length > 0 && (
                  <div style={{ padding: '10px 0 4px 32px', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.08em', color: C.muted, textTransform: 'uppercase' }}>
                    Global values ({globalVals.length}) — exclude to hide from this client
                  </div>
                )}
                {!valLoading && annotated.filter(v => v.is_global).map(v => (
                  <ValueRow key={v.id} val={v} listId={selList.id} entryMode={entryMode} isClientView={isClientView} onRefresh={refresh} />
                ))}

                {!valLoading && isInherited && clientVals.length > 0 && (
                  <div style={{ padding: '14px 0 4px 32px', fontSize: 10, fontWeight: 700,
                    letterSpacing: '0.08em', color: C.muted, textTransform: 'uppercase' }}>
                    Client-specific values ({clientVals.length})
                  </div>
                )}
                {!valLoading && annotated.filter(v => !v.is_global).map(v => (
                  <ValueRow key={v.id} val={v} listId={selList.id} entryMode={entryMode} isClientView={isClientView} onRefresh={refresh} />
                ))}

                {!valLoading && values.length === 0 && (
                  <div style={{ textAlign: 'center', color: C.muted, fontSize: 13, padding: '40px 0' }}>
                    No values yet — add one above
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {pendingCodes && (
        <AddCodesDialog
          list={selList}
          values={pendingCodes}
          onCancel={() => setPendingCodes(null)}
          onDone={() => {
            setPendingCodes(null);
            setSelList(prev => ({ ...prev, entry_mode: 'code_value' }));
            setLists(prev => prev.map(l => l.id === selList.id ? { ...l, entry_mode: 'code_value' } : l));
            setAddInput(''); setAddCode(''); setAddErr('');
            refresh();
          }}
        />
      )}
      {confirmUI}
    </div>
  );
}
